import { ChatChannelIds, ChatMessage as ChatMessageType, ClientType } from '@core/types';
import { Button, Options } from '@core/ui';
import { useAppSelector } from '@online-webinar/store';
import { useCallback } from 'react';
import { OpenChatMessageMenu } from '../../../types/components/OpenChatMessageMenu';
import { ChatUserAvatarPic, ChatUserOnlineStatus, GlobalLabel, MessageDeleted, MessageText } from '../../atoms';
import { ChatMessage, ChatMessages } from '../../organisms';
import { ChatUserAvatar } from '../ChatUserAvatar';
import { MessageBody } from '../MessageBody';
import { MessageContent } from '../MessageContent';
import { MessageInfo } from '../MessageInfo';
import { RelatedMessageContent } from '../RelatedMessageContent';

export const ActiveChatChannel = ({
    channelId,
    openChatMessageMenu,
    setOpenChatMessageMenu
}: {
    channelId: string;
    openChatMessageMenu: OpenChatMessageMenu | null;
    setOpenChatMessageMenu: (message: OpenChatMessageMenu | null) => void;
}) => {
    const channel = useAppSelector((state) => state.liveWebinar.chatChannels[channelId]);
    const me = useAppSelector((state) => state.liveWebinar.me);

    const onOpenChatMessageMenu = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, message: ChatMessageType) => {
            e.stopPropagation();

            if (openChatMessageMenu?.message?.messageId === message.messageId) {
                setOpenChatMessageMenu(null);
                return;
            }

            const boundingRect = e.currentTarget.getBoundingClientRect();
            setOpenChatMessageMenu({
                me: me,
                message: message,
                channelId: channelId,
                position: {
                    x: boundingRect.x,
                    y: boundingRect.y,
                    height: boundingRect.height
                },
                parent: e.currentTarget
            });
        },
        [openChatMessageMenu, channelId, setOpenChatMessageMenu]
    );

    return (
        <ChatMessages
            channelId={channelId}
            classNames="col-start-1 row-start-2 h-full"
            messages={channel?.messages ?? []}
            itemContent={(index: number, message: ChatMessageType) => (
                <ChatMessage key={message.messageId} ownMessage={me?.participantId === message.senderParticipantId}>
                    <ChatUserAvatar>
                        <ChatUserAvatarPic />
                        <ChatUserOnlineStatus />
                    </ChatUserAvatar>
                    <MessageContent ownMessage={me?.participantId === message.senderParticipantId}>
                        <MessageInfo username={message.senderNickname} time={message.timestamp} />
                        <MessageBody>
                            {!message.isDeleted && message.relatedMessage && (
                                <RelatedMessageContent>
                                    <MessageInfo
                                        relationType={message.relationType}
                                        username={message.relatedMessage.senderNickname}
                                        time={message.relatedMessage.timestamp}
                                    />
                                    <MessageBody>
                                        {!message.relatedMessage?.isDeleted && <MessageText messageBody={message.relatedMessage.message} />}
                                        {message.relatedMessage?.isDeleted && <MessageDeleted />}
                                    </MessageBody>
                                </RelatedMessageContent>
                            )}
                            <GlobalLabel isGlobal={message.channelId === ChatChannelIds.publicChannel} />
                            {!message.isDeleted && <MessageText messageBody={message.message} />}
                            {message.isDeleted && <MessageDeleted />}
                        </MessageBody>
                    </MessageContent>
                    {message.isDeleted && (me?.clientType === ClientType.moderator || me?.clientType === ClientType.presenter) && (
                        <Button iconbtn onClick={(e) => onOpenChatMessageMenu(e, message)}>
                            <Options classNames="size-6" />
                        </Button>
                    )}
                    {!message.isDeleted && (
                        <Button iconbtn onClick={(e) => onOpenChatMessageMenu(e, message)}>
                            <Options classNames="size-6" />
                        </Button>
                    )}
                </ChatMessage>
            )}
        />
    );
};
